import api from "./api";

export interface LeaveType {
  id: string;
  name: string;
  code: string;
  description: string;
  category: string;
  isPaid: boolean;
  requiresApproval: boolean;
  requiresDocumentation: boolean;
  documentationRequiredAfterDays: number | null;
  color: string;
  icon: string;
  defaultDaysPerYear: number | null;
  maxDaysPerYear: number | null;
  maxConsecutiveDays: number | null;
  minDaysNotice: number | null;
  allowHalfDay: boolean;
  allowNegativeBalance: boolean;
}

export interface LeaveRequest {
  id: string;
  userId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  isHalfDayStart: boolean;
  isHalfDayEnd: boolean;
  reason: string;
  contactDuringLeave: string | null;
  emergencyContact: string | null;
  status: string;
  approverId: string | null;
  approverComments: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  leaveType?: LeaveType;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    employeeId: string;
  };
}

export interface CreateLeaveRequestData {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  isHalfDayStart?: boolean;
  isHalfDayEnd?: boolean;
  reason: string;
  contactDuringLeave?: string;
  emergencyContact?: string;
}

export interface UpdateLeaveRequestData {
  startDate?: string;
  endDate?: string;
  isHalfDayStart?: boolean;
  isHalfDayEnd?: boolean;
  reason?: string;
  contactDuringLeave?: string;
  emergencyContact?: string;
}

export interface LeaveRequestFilter {
  status?: string;
  leaveTypeId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface LeaveTypesResponse {
  leaveTypes: LeaveType[];
  total: number;
}

export interface LeaveRequestsResponse {
  requests: LeaveRequest[];
  total: number;
}

export interface LeaveRequestResponse {
  request: LeaveRequest;
  message?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  role: string;
  employeeId: string;
  departmentId?: string;
}

export interface UsersResponse {
  users: User[];
  total: number;
}

export interface LeaveBalance {
  id: string;
  userId: string;
  leaveTypeId: string;
  year: number;
  totalEntitlement: number | null;
  carriedOverDays: number | null;
  adjustmentDays: number | null;
  usedDays: number | null;
  pendingDays: number | null;
  availableDays: number | null;
  accruedDays: number | null;
  leaveType?: LeaveType;
}

export class LeaveService {
  /**
   * Get all active leave types
   */
  async getLeaveTypes(): Promise<LeaveTypesResponse> {
    console.log("🔍 Fetching leave types from API...");
    try {
      const response = await api.get<{ data: LeaveTypesResponse }>(
        "/api/v1/leaves/types"
      );
      console.log("✅ Leave types fetched successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to fetch leave types:", error);
      throw error;
    }
  }

  /**
   * Get leave requests for current user
   */
  async getLeaveRequests(
    filter?: LeaveRequestFilter & {
      userId?: string;
      allUsers?: string;
    }
  ): Promise<LeaveRequestsResponse> {
    console.log("🔍 Fetching leave requests...", filter);
    try {
      const params = new URLSearchParams();

      if (filter?.status) params.append("status", filter.status);
      if (filter?.leaveTypeId) params.append("leaveTypeId", filter.leaveTypeId);
      if (filter?.startDate) params.append("startDate", filter.startDate);
      if (filter?.endDate) params.append("endDate", filter.endDate);
      if (filter?.limit) params.append("limit", filter.limit.toString());
      if (filter?.offset) params.append("offset", filter.offset.toString());
      if (filter?.userId) params.append("userId", filter.userId);
      if (filter?.allUsers) params.append("allUsers", filter.allUsers);

      const response = await api.get<{ data: LeaveRequestsResponse }>(
        `/api/v1/leaves?${params.toString()}`
      );
      console.log("✅ Leave requests fetched successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to fetch leave requests:", error);
      throw error;
    }
  }

  /**
   * Get a single leave request by ID
   */
  async getLeaveRequest(id: string): Promise<LeaveRequest> {
    console.log("🔍 Fetching leave request:", id);
    try {
      const response = await api.get<{ data: { request: LeaveRequest } }>(
        `/api/v1/leaves/${id}`
      );
      console.log("✅ Leave request fetched successfully:", response.data);
      return response.data.data.request;
    } catch (error) {
      console.error("❌ Failed to fetch leave request:", error);
      throw error;
    }
  }

  /**
   * Create a new leave request
   */
  async createLeaveRequest(
    data: CreateLeaveRequestData & { targetUserId?: string }
  ): Promise<LeaveRequestResponse> {
    console.log("🔍 Creating leave request...", data);
    try {
      const response = await api.post<{ data: LeaveRequestResponse }>(
        "/api/v1/leaves",
        data
      );
      console.log("✅ Leave request created successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to create leave request:", error);
      throw error;
    }
  }

  /**
   * Update a leave request
   */
  async updateLeaveRequest(
    id: string,
    data: UpdateLeaveRequestData
  ): Promise<LeaveRequestResponse> {
    console.log("🔍 Updating leave request:", id, data);
    try {
      const response = await api.patch<{ data: LeaveRequestResponse }>(
        `/api/v1/leaves/${id}`,
        data
      );
      console.log("✅ Leave request updated successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to update leave request:", error);
      throw error;
    }
  }

  /**
   * Cancel a leave request
   */
  async cancelLeaveRequest(id: string): Promise<LeaveRequestResponse> {
    console.log("🔍 Cancelling leave request:", id);
    try {
      const response = await api.delete<{ data: LeaveRequestResponse }>(
        `/api/v1/leaves/${id}`
      );
      console.log("✅ Leave request cancelled successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to cancel leave request:", error);
      throw error;
    }
  }

  /**
   * Approve a leave request (Admin, HR, Manager only)
   */
  async approveLeaveRequest(
    id: string,
    comments?: string
  ): Promise<LeaveRequestResponse> {
    console.log("🔍 Approving leave request:", id, { comments });
    try {
      const response = await api.post<{ data: LeaveRequestResponse }>(
        `/api/v1/leaves/${id}/approve`,
        { comments }
      );
      console.log("✅ Leave request approved successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to approve leave request:", error);
      throw error;
    }
  }

  /**
   * Reject a leave request (Admin, HR, Manager only)
   */
  async rejectLeaveRequest(
    id: string,
    comments: string
  ): Promise<LeaveRequestResponse> {
    console.log("🔍 Rejecting leave request:", id, { comments });
    try {
      const response = await api.post<{ data: LeaveRequestResponse }>(
        `/api/v1/leaves/${id}/reject`,
        { comments }
      );
      console.log("✅ Leave request rejected successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to reject leave request:", error);
      throw error;
    }
  }

  /**
   * Get users list (for admin/HR to create leave requests on behalf)
   */
  async getUsers(): Promise<UsersResponse> {
    console.log("🔍 Fetching users for admin/HR...");
    try {
      const response = await api.get<{ data: UsersResponse }>(
        "/api/v1/leaves/users"
      );
      console.log("✅ Users fetched successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to fetch users:", error);
      throw error;
    }
  }

  /**
   * Get all leave types (with option to include inactive for admin)
   */
  async getAllLeaveTypes(includeInactive = false): Promise<LeaveTypesResponse> {
    console.log("🔍 Fetching all leave types...", { includeInactive });
    try {
      const params = includeInactive ? "?includeInactive=true" : "";
      const response = await api.get<{ data: LeaveTypesResponse }>(
        `/api/v1/leaves/types${params}`
      );
      console.log("✅ All leave types fetched successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to fetch all leave types:", error);
      throw error;
    }
  }

  /**
   * Get a single leave type by ID
   */
  async getLeaveTypeById(id: string): Promise<LeaveType> {
    console.log("🔍 Fetching leave type:", id);
    try {
      const response = await api.get<{ data: { leaveType: LeaveType } }>(
        `/api/v1/leaves/types/${id}`
      );
      console.log("✅ Leave type fetched successfully:", response.data);
      return response.data.data.leaveType;
    } catch (error) {
      console.error("❌ Failed to fetch leave type:", error);
      throw error;
    }
  }

  /**
   * Create a new leave type (Admin only)
   */
  async createLeaveType(data: {
    name: string;
    code: string;
    description?: string;
    color?: string;
    defaultDaysPerYear: number;
    maxDaysPerYear: number;
    maxDaysPerRequest: number;
    requiresApproval?: boolean;
    requiresDocument?: boolean;
    allowHalfDay?: boolean;
    advanceNoticeDays?: number;
    isPaid?: boolean;
    isActive?: boolean;
    allowCarryOver?: boolean;
  }): Promise<{ leaveType: LeaveType; message: string }> {
    console.log("🔍 Creating leave type...", data);
    try {
      const response = await api.post<{
        data: { leaveType: LeaveType; message: string };
      }>("/api/v1/leaves/types", data);
      console.log("✅ Leave type created successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to create leave type:", error);
      throw error;
    }
  }

  /**
   * Update a leave type (Admin only)
   */
  async updateLeaveType(
    id: string,
    data: {
      name?: string;
      code?: string;
      description?: string;
      color?: string;
      defaultDaysPerYear?: number;
      maxDaysPerYear?: number;
      maxDaysPerRequest?: number;
      requiresApproval?: boolean;
      requiresDocument?: boolean;
      allowHalfDay?: boolean;
      advanceNoticeDays?: number;
      isPaid?: boolean;
      isActive?: boolean;
      allowCarryOver?: boolean;
    }
  ): Promise<{ leaveType: LeaveType; message: string }> {
    console.log("🔍 Updating leave type:", id, data);
    try {
      const response = await api.patch<{
        data: { leaveType: LeaveType; message: string };
      }>(`/api/v1/leaves/types/${id}`, data);
      console.log("✅ Leave type updated successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to update leave type:", error);
      throw error;
    }
  }

  /**
   * Delete/Deactivate a leave type (Admin only - soft delete)
   */
  async deleteLeaveType(
    id: string
  ): Promise<{ leaveType: LeaveType; message: string }> {
    console.log("🔍 Deleting leave type:", id);
    try {
      const response = await api.delete<{
        data: { leaveType: LeaveType; message: string };
      }>(`/api/v1/leaves/types/${id}`);
      console.log("✅ Leave type deleted successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to delete leave type:", error);
      throw error;
    }
  }

  /**
   * Get leave balances for a user (Admin/HR only)
   */
  async getLeaveBalances(userId: string): Promise<LeaveBalance[]> {
    console.log("🔍 Fetching leave balances for user:", userId);
    try {
      const response = await api.get<{ data: LeaveBalance[] }>(
        `/api/v1/admin/leave-balances/${userId}`
      );
      console.log("✅ Leave balances fetched successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to fetch leave balances:", error);
      throw error;
    }
  }

  /**
   * Update leave balance for a user (Admin/HR only)
   */
  async updateLeaveBalance(data: {
    userId: string;
    leaveTypeId: string;
    totalDays: number;
    year: number;
    carriedOverDays?: number;
    adjustmentDays?: number;
  }): Promise<{ balance: LeaveBalance; message: string }> {
    console.log("🔍 Updating leave balance...", data);
    try {
      const response = await api.put<{
        data: { balance: LeaveBalance; message: string };
      }>("/api/v1/admin/leave-balances", data);
      console.log("✅ Leave balance updated successfully:", response.data);
      return response.data.data;
    } catch (error) {
      console.error("❌ Failed to update leave balance:", error);
      throw error;
    }
  }

  // ========== Dashboard Statistics APIs ==========

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<{
    today: { total: number; sick: number; vacation: number; personal: number };
    thisWeek: {
      total: number;
      sick: number;
      vacation: number;
      personal: number;
    };
    thisMonth: {
      total: number;
      sick: number;
      vacation: number;
      personal: number;
    };
    thisYear: {
      total: number;
      sick: number;
      vacation: number;
      personal: number;
    };
  }> {
    console.log("🔍 Fetching dashboard stats...");
    try {
      const response = await api.get("/api/v1/dashboard/stats");
      console.log("✅ Dashboard stats fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to fetch dashboard stats:", error);
      throw error;
    }
  }

  /**
   * Get monthly trend data
   */
  async getMonthlyTrend(months = 6): Promise<
    Array<{
      month: string;
      sick: number;
      vacation: number;
      personal: number;
    }>
  > {
    console.log("🔍 Fetching monthly trend...", { months });
    try {
      const response = await api.get(
        `/api/v1/dashboard/monthly-trend?months=${months}`
      );
      console.log("✅ Monthly trend fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to fetch monthly trend:", error);
      throw error;
    }
  }

  /**
   * Get conflicting leaves (dates where multiple employees are on leave)
   */
  async getConflictingLeaves(): Promise<
    Array<{
      date: string;
      employees: string[];
      count: number;
    }>
  > {
    console.log("🔍 Fetching conflicting leaves...");
    try {
      const response = await api.get("/api/v1/dashboard/conflicting-leaves");
      console.log("✅ Conflicting leaves fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to fetch conflicting leaves:", error);
      throw error;
    }
  }

  /**
   * Get employee leave balances
   */
  async getEmployeeBalances(userId?: string): Promise<
    Array<{
      id: string;
      name: string;
      sick: number;
      vacation: number;
      personal: number;
    }>
  > {
    console.log("🔍 Fetching employee balances...", { userId });
    try {
      const params = userId ? `?userId=${userId}` : "";
      const response = await api.get(
        `/api/v1/dashboard/employee-balances${params}`
      );
      console.log("✅ Employee balances fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to fetch employee balances:", error);
      throw error;
    }
  }
}

export const leaveService = new LeaveService();
export default leaveService;
